import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiLogin, getMyProfile, startImpersonationSession, stopImpersonationSession } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const persistSession = useCallback((nextToken, nextUser) => {
    setToken(nextToken);
    setUser(nextUser);

    if (nextToken) {
      localStorage.setItem('dsa_token', nextToken);
    } else {
      localStorage.removeItem('dsa_token');
    }

    if (nextUser) {
      localStorage.setItem('dsa_user', JSON.stringify(nextUser));
    } else {
      localStorage.removeItem('dsa_user');
    }
  }, []);

  useEffect(() => {
    let active = true;
    const storedUser = localStorage.getItem('dsa_user');
    const storedToken = localStorage.getItem('dsa_token');

    const hydrateSession = async () => {
      if (!storedToken) {
        if (active) setLoading(false);
        return;
      }

      try {
        const resp = await getMyProfile(storedToken);
        const serverUser = resp?.data || null;
        if (!active || !serverUser) return;

        persistSession(storedToken, serverUser);
      } catch {
        if (!active) return;
        persistSession(null, null);
      } finally {
        if (active) setLoading(false);
      }
    };

    if (storedUser && localStorage.getItem('dsa_token')) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        localStorage.removeItem('dsa_user');
      }
    }

    hydrateSession();
    return () => {
      active = false;
    };
  }, [persistSession]);

  const login = useCallback(async (email, password) => {
    try {
      const data = await apiLogin(email, password);
      const nextUser = data.user;
      persistSession(data.token, nextUser);
      return { success: true, user: nextUser };
    } catch (err) {
      return { success: false, error: err.message || 'Login failed' };
    }
  }, [persistSession]);

  const logout = useCallback(() => {
    persistSession(null, null);
  }, [persistSession]);

  const updateProfile = useCallback((partial) => {
    setUser((currentUser) => {
      if (!currentUser) return currentUser;
      const nextUser = { ...currentUser, ...partial };
      localStorage.setItem('dsa_user', JSON.stringify(nextUser));
      return nextUser;
    });
  }, []);

  const startImpersonation = useCallback(async (targetUserId) => {
    if (!token) {
      return { success: false, error: 'Please log in again.' };
    }

    try {
      const resp = await startImpersonationSession(token, targetUserId);
      const nextUser = resp?.data?.user || null;
      const nextToken = resp?.data?.token || null;
      if (!nextUser || !nextToken) {
        throw new Error('Impersonation session could not be started');
      }
      persistSession(nextToken, nextUser);
      return { success: true, user: nextUser };
    } catch (err) {
      return { success: false, error: err.message || 'Failed to start impersonation' };
    }
  }, [persistSession, token]);

  const stopImpersonation = useCallback(async () => {
    if (!token) {
      return { success: false, error: 'Please log in again.' };
    }

    try {
      const resp = await stopImpersonationSession(token);
      const nextUser = resp?.data?.user || null;
      const nextToken = resp?.data?.token || null;
      if (!nextUser || !nextToken) {
        throw new Error('Original session could not be restored');
      }
      persistSession(nextToken, nextUser);
      return { success: true, user: nextUser };
    } catch (err) {
      return { success: false, error: err.message || 'Failed to exit impersonation' };
    }
  }, [persistSession, token]);

  const value = useMemo(() => ({
    user,
    token,
    login,
    logout,
    updateProfile,
    startImpersonation,
    stopImpersonation,
    loading,
    isAuthenticated: !!user,
  }), [user, token, login, logout, updateProfile, startImpersonation, stopImpersonation, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
